import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useScript } from '../context/ScriptContext';
import { useAuth } from '../context/AuthContext';
import { Product } from '../types';
import * as api from '../services/api';
import { ApiError } from '../services/api';
import ImageUpload from '../components/ImageUpload';
import { PRODUCT_CATEGORIES, categoryLabel } from '../utils/productCategories';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const emptyForm = {
  imageUrl: '',
  name: '',
  description: '',
  price: '',
  category: 'melem',
  unit: '',
  inStock: true,
};

const DeliveriesEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useScript();
  const { token } = useAuth();
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [nameError, setNameError] = useState('');

  const isEdit = Boolean(id);
  useDocumentTitle(isEdit ? 'Уређивање производа — Манастир Градац' : 'Нови производ — Манастир Градац');

  useEffect(() => {
    if (!id) return;
    api.getProductById(Number(id))
      .then(item => setForm({
        imageUrl: item.imageUrl,
        name: item.name,
        description: item.description,
        price: item.price,
        category: item.category || 'melem',
        unit: item.unit,
        inStock: item.inStock,
      }))
      .catch((err: ApiError) => setError(err?.message || t('Не могу учитати производ.')));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    setNameError('');
    if (!form.name.trim()) {
      setNameError(t('Назив је обавезан.'));
      return;
    }

    setSaving(true);
    setError('');
    try {
      if (isEdit && id) {
        await api.updateProduct(token, Number(id), form);
      } else {
        await api.createProduct(token, form as Omit<Product, 'id' | 'sortOrder'>);
      }
      navigate('/isporuke');
    } catch (err) {
      const apiErr = err as ApiError;
      setError(apiErr?.message || t('Грешка при чувању. Покушај поново.'));
      setSaving(false);
    }
  };

  const set = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm(prev => ({ ...prev, [field]: e.target.value }));

  return (
    <div className="news-edit-page">
      <form className="news-edit-layout" onSubmit={handleSubmit}>
        <div className="news-edit-form-col">
          <h1 className="news-heading" style={{ textAlign: 'left', marginBottom: '28px' }}>
            {isEdit ? t('Уреди производ') : t('Нови производ')}
          </h1>

          {error && <div className="form-error-banner" role="alert">{error}</div>}

          <ImageUpload
            label="URL slike"
            imageUrl={form.imageUrl}
            onImageUrlChange={url => setForm(prev => ({ ...prev, imageUrl: url }))}
          />

          <label className="post-admin-label">{t('Назив')}</label>
          <input
            className="news-edit-input"
            type="text"
            value={form.name}
            onChange={e => {
              if (nameError) setNameError('');
              set('name')(e);
            }}
            aria-invalid={Boolean(nameError)}
          />
          {nameError && <div className="field-error" role="alert">{nameError}</div>}

          <label className="post-admin-label">{t('Опис')}</label>
          <textarea
            className="news-edit-textarea"
            rows={6}
            value={form.description}
            onChange={set('description')}
          />

          <label className="post-admin-label">{t('Категорија')}</label>
          <select className="news-edit-input" value={form.category} onChange={set('category')}>
            {PRODUCT_CATEGORIES.map(c => (
              <option key={c.value} value={c.value}>{t(c.label)}</option>
            ))}
          </select>

          <label className="post-admin-label">{t('Цена')}</label>
          <input
            className="news-edit-input"
            type="text"
            placeholder={t('нпр. 500 РСД')}
            value={form.price}
            onChange={set('price')}
          />

          <label className="post-admin-label">{t('Паковање / јединица')}</label>
          <input
            className="news-edit-input"
            type="text"
            placeholder={t('нпр. 50 ml')}
            value={form.unit}
            onChange={set('unit')}
          />

          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px' }}>
            <input type="checkbox" checked={form.inStock}
              onChange={e => setForm(prev => ({ ...prev, inStock: e.target.checked }))} />
            {t('Доступно (на стању)')}
          </label>

          <div style={{ height: '80px' }} />
        </div>

        <div className="news-edit-preview-col">
          <p className="post-admin-label" style={{ marginBottom: '10px' }}>{t('Предпреглед')}</p>
          <div className="news-edit-preview-card">
            {form.imageUrl ? (
              <div className="news-image" style={{ aspectRatio: '16/10', height: 'auto' }}>
                <img src={form.imageUrl} alt={form.name} width={800} height={500} decoding="async" />
                {form.category && <div className="product-category-badge">{t(categoryLabel(form.category))}</div>}
              </div>
            ) : (
              <div className="news-image news-image-placeholder" />
            )}
            <div className="news-body">
              <h3 className="news-title">{form.name || t('Назив производа...')}</h3>
              <p className="news-excerpt">{form.description || t('Опис...')}</p>
              <div className="product-meta">
                {form.price && <span className="product-price">{form.price}</span>}
                {form.unit && <span className="product-unit">{form.unit}</span>}
              </div>
            </div>
          </div>
        </div>

        <div className="news-edit-sticky-bar">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? t('Чување...') : t('Сачувај')}
          </button>
          <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>
            {t('Откажи')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DeliveriesEditPage;
